import { useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";

import { Select, OptionType } from "ui/select";
import BatchSelectOptionLabel from "containers/admin/clients/client/approval-queue/components/add-to-batch-select-form/batch-select-option-label";

import { getBatchStatuses } from "containers/admin/clients/client/approval-queue/approval-queue.helpers";
import { useAddToBatchSelect } from "containers/admin/clients/client/approval-queue/hooks/use-add-to-batch-select";

type BatchOption = OptionType & {
  label: string;
  versionStatuses: ReturnType<typeof getBatchStatuses>;
  isCurrentBatch?: boolean;
};

export type AddToBatchSelectFormProps = {
  latestBatchId?: string;
  isLatestBatchLoading?: boolean;
  setIsSubmitDisabled: (isDisabled: boolean) => void;
  /** When set, marks the version's existing batch in the dropdown (change-batch flow). */
  versionBatchId?: string;
  /** When false, includes approved batches. Defaults to BUILDING and IN_REVIEW only. */
  filterBatchesByActiveStatus?: boolean;
  /** Creative modal tracks batch selection outside react-hook-form for submit. */
  onBatchIdChange?: (batchId: string, batchName?: string) => void;
};

const AddToBatchSelectForm = ({
  latestBatchId,
  isLatestBatchLoading = false,
  setIsSubmitDisabled,
  versionBatchId,
  filterBatchesByActiveStatus = true,
  onBatchIdChange,
}: AddToBatchSelectFormProps) => {
  const { control } = useFormContext();

  const { batches, batchStatuses, isSelectLoading } = useAddToBatchSelect({
    isLatestBatchLoading,
    latestBatchId,
    versionBatchId,
    setIsSubmitDisabled,
    filterBatchesByActiveStatus,
    onBatchIdChange,
  });

  const options = useMemo(() => {
    if (isSelectLoading) {
      return [];
    }

    return batches.map((batch) => ({
      label: batch.name,
      value: batch.id,
      versionStatuses: getBatchStatuses(batchStatuses, batch.status),
      isCurrentBatch: !!versionBatchId && batch.id === versionBatchId,
    }));
  }, [batches, batchStatuses, isSelectLoading, versionBatchId]);

  return (
    <Controller
      name="batchId"
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <Select
          {...field}
          label="Batch name"
          placeholder={isSelectLoading ? "Loading..." : "Select..."}
          ariaLabel="Batch name"
          selectName={`batch-select-${field.name}`}
          options={options}
          required
          formatOptionLabel={(option, { context }) => {
            const batchOption = option as BatchOption;

            return (
              <BatchSelectOptionLabel
                batchName={batchOption.label}
                versionStatuses={batchOption.versionStatuses}
                isCurrentBatch={batchOption.isCurrentBatch}
                context={context}
              />
            );
          }}
          value={options.find((opt) => opt.value === field.value) || null}
          disabled={isSelectLoading}
          onChange={(selectedOption) => {
            const batchId = selectedOption?.value as string;
            field.onChange(batchId);
            if (batchId) {
              const selectedBatch = batches.find((batch) => batch.id === batchId);
              onBatchIdChange?.(batchId, selectedBatch?.name);
            }
          }}
        />
      )}
    />
  );
};

export default AddToBatchSelectForm;
